import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import classNames from 'classnames';

import IconHeart2 from 'components/icons/iconHeart2';
import { PDP_FILTER_TYPES } from 'commons/constants';
import { productFilterTypes } from 'types/PDPTypes';
import FlyoutContainer from 'components/consumer/FlyoutContainer';
import FlatButtonFilters from 'components/consumer/FlatButtonFilters';
import usePostHogFeatureFlag from 'hooks/usePostHogFeatureFlag';
import isNewConsumerApp from 'commons/isNewConsumerApp';

export const flyOutConfig = {
  'pet-friendly': {
    imageUrl: 'https://joybird2.imgix.net/public_img/pet-friendly.png',
    content: `Designed for the most active of homes, 
                these pet and kid-friendly fabrics don't 
                flinch at the sight of crumbs, fur, spills 
                and stains. However, don't let that toughness 
                fool you. They're still the soft, cozy upholstery 
                your shedding pets, sticky children, and 
                clutziest houseguests deserve to lounge on.`,
    title: 'Pet-friendly fabrics',
  },
  safeguard: {
    videoObj: {
      controls: false,
      autoPlay: true,
      loop: true,
      link: 'https://joybird2.imgix.net/public_img/safeguard-fabrics_480p.mp4',
    },
    content: `We created our performance fabrics collection 
                engineered to withstand kids, pets, and everyday life. 
                Soft, stain-resistant, durable, and most importantly, 
                easy to clean, we guarantee our Joybird Safeguard 
                fabrics are as long lasting as they are beautiful.`,
    title: 'Safeguard fabrics',
  },
  'greenguard-gold': {
    imageUrl: 'https://joybird2.imgix.net/public_img/Greenguard-image.jpg',
    content: `These fabrics have achieved the GREENGUARD Gold Certification,
               and are scientifically proven to meet some of the world's most
               rigorous, third-party chemical emissions standards, helping to
               reduce indoor air pollution and the risk of chemical exposure,
               while aiding in the creation of healthier indoor environments.`,
    title: 'GREENGUARD Gold Textile',
  },
};

const PDPUpholsteryFilters = ({
  className = '',
  fabricFavInfoToShow,
  filterCounts = {},
  hideFlyout = false,
  isClearanceFabricAvailable = false,
  loading = false,
  selectedItem,
  setSelectedItem,
  variant,
}) => {
  // Posthog Experiment
  const pdpFabricFilters = usePostHogFeatureFlag('PDPFabricFilters');
  const isMultiSelectFilters = pdpFabricFilters === 'test' || isNewConsumerApp;
  const [flyOutContent, setFlyOutContent] = useState({});
  const [isFlyOutVisible, setIsFlyOutVisible] = useState(false);

  const containerRef = useRef(null);
  const iconHeart = (
    <span className="flex items-center justify-center [&_svg]:w-[1.1rem] [&_svg]:h-[1.1rem] [&_svg_path]:fill-[#d86262] [&_svg_path]:stroke-[#d86262]">
      <IconHeart2 />
      &nbsp;
      {`(${filterCounts[PDP_FILTER_TYPES.FAVORITES]})`}
    </span>
  );

  const getStandardItems = multiSelect => {
    const items = [
      { label: iconHeart, id: PDP_FILTER_TYPES.FAVORITES },
      { label: 'Popular', id: PDP_FILTER_TYPES.POPULAR },
      { label: 'Velvets', id: PDP_FILTER_TYPES.VELVET },
      { label: 'Pet-friendly', id: PDP_FILTER_TYPES.PETFRIENDLY },
      { label: 'Safeguard', id: PDP_FILTER_TYPES.SAFEGUARD },
      { label: 'Sustainable', id: PDP_FILTER_TYPES.SUSTAINABLE },
      { label: 'GREENGUARD Gold', id: PDP_FILTER_TYPES.GREENGUARD },
    ];
    if (!multiSelect) {
      items.splice(2, 0, { label: 'All', id: PDP_FILTER_TYPES.ALL });
    }
    return items;
  };

  const getItems = (multiSelect, clearanceAvailable) => {
    const items = getStandardItems(multiSelect);
    if (clearanceAvailable) {
      items.push({ label: 'Clearance', id: PDP_FILTER_TYPES.CLEARANCE });
    }
    if (multiSelect)
      items.push({
        label: 'Clear all',
        id: PDP_FILTER_TYPES.CLEARALL,
        color: 'red',
      });
    return items;
  };

  const items = getItems(isMultiSelectFilters, isClearanceFabricAvailable);

  if (loading || isEmpty(filterCounts)) {
    return <FlatButtonFilters loading />;
  }

  const toggleFlyOut = id => {
    if (hideFlyout) {
      return;
    }
    if (id === 'pet-friendly') {
      setFlyOutContent({
        top: 0,
        ...flyOutConfig['pet-friendly'],
      });
      setIsFlyOutVisible(true);
    } else if (id === 'safeguard') {
      setFlyOutContent({
        top: 0,
        ...flyOutConfig.safeguard,
      });
      setIsFlyOutVisible(true);
    } else if (id === 'greenguard-gold') {
      setFlyOutContent({
        top: 0,
        ...flyOutConfig['greenguard-gold'],
      });
      setIsFlyOutVisible(true);
    }
  };

  const onClose = () => setIsFlyOutVisible(false);

  const finalItems = items.filter(filterItem => {
    // keep favorites filter even if count is 0.
    if (
      filterCounts[filterItem.id] ||
      (filterItem.id === PDP_FILTER_TYPES.FAVORITES &&
        filterCounts[PDP_FILTER_TYPES.FAVORITES] !== undefined)
    ) {
      return true;
    }
    if (filterItem.id === PDP_FILTER_TYPES.CLEARANCE) {
      // only push this when its a clearance product
      return true;
    }
    if (filterItem.id === PDP_FILTER_TYPES.CLEARALL) {
      return true;
    }
    return false;
  });
  return (
    <div
      className={classNames({
        [className]: !!className,
        'max-[1023px]:overflow-x-scroll max-[1023px]:overflow-y-hidden max-[1023px]:ml-5 max-[1023px]:[scrollbar-width:none] max-[1023px]:[-ms-overflow-style:none] max-[1023px]:[transition:margin_.5s]':
          variant === 'PDPSelectUpholstery',
      })}
      ref={containerRef}
    >
      {isFlyOutVisible && !hideFlyout ? (
        <FlyoutContainer
          top={flyOutContent.top}
          imageUrl={flyOutContent.imageUrl}
          videoObj={flyOutContent.videoObj}
          content={flyOutContent.content}
          title={flyOutContent.title}
          onClose={onClose}
          isOpen={isFlyOutVisible}
        />
      ) : null}
      <FlatButtonFilters
        items={finalItems}
        selectedItem={selectedItem}
        setSelectedItem={id => {
          setSelectedItem(id);
        }}
        onMouseEnter={id => {
          toggleFlyOut(id);
        }}
        onMouseLeave={onClose}
        isSelectedButtonFilled
        variant={variant}
      />
      {fabricFavInfoToShow ? (
        <div className="text-xs font-bold max-[1023px]:pl-[22px]">
          {fabricFavInfoToShow}
        </div>
      ) : null}
    </div>
  );
};

PDPUpholsteryFilters.propTypes = {
  className: PropTypes.string,
  fabricFavInfoToShow: PropTypes.string,
  filterCounts: productFilterTypes,
  hideFlyout: PropTypes.bool,
  isClearanceFabricAvailable: PropTypes.bool,
  loading: PropTypes.bool,
  selectedItem: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  setSelectedItem: PropTypes.func,
  variant: PropTypes.oneOf(['PDPSelectUpholstery']),
};

export default PDPUpholsteryFilters;
